"use client";
import React, { useState, useId } from "react";
import "./index.css";
import { usePathname, useSearchParams } from "next/navigation";
import axios from "axios";
import toast from "react-hot-toast";

import CustomButton from "../CustomButton";
import { Form, FloatingLabel } from "react-bootstrap";

const CustomForm = ({ onHide = () => {}, categoryId, productId, variant = "modal-form" }) => {
  const nameId = useId();
  const phoneId = useId();
  const emailId = useId();
  const messageId = useId();

  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    contact_no: "",
    contact_message: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "contact_no") {
      value = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "contact_name") {
      value = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validate = () => {
    let newErrors = {};

    if (!formData.contact_name.trim()) {
      newErrors.contact_name = "Name is required";
    } else if (formData.contact_name.trim().length < 3) {
      newErrors.contact_name = "Name must be at least 3 characters";
    }

    if (!formData.contact_no.trim()) {
      newErrors.contact_no = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.contact_no.trim())) {
      newErrors.contact_no = "Enter a valid 10-digit mobile number";
    }

    if (!formData.contact_email.trim()) {
      newErrors.contact_email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.contact_email.trim())) {
      newErrors.contact_email = "Enter a valid email address";
    }

    if (!formData.contact_message.trim()) {
      newErrors.contact_message = "Message is required";
    } else if (formData.contact_message.trim().length < 10) {
      newErrors.contact_message = "Message must be at least 10 characters";
    }

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Final Submit Handler
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);

    const currentUrl = `${window.location.origin}${pathname}${searchParams.toString() ? "?" + searchParams.toString() : ""}`;

    const payload = {
      organization_key: process.env.NEXT_PUBLIC_ORGANIZATION_KEY,
      category_id: categoryId,
      product_id: productId,
      posted_url: currentUrl,
      ...formData,
    };

    try {
      // Call both APIs concurrently
      const [emailResponse, enquiryResponse] = await Promise.all([
        axios.post("/api/send-email", payload),
        axios.post(`${API_URL}/api/submit-enquiry`, payload)
      ]);

      if (enquiryResponse.data.success === true) {
        toast.success(enquiryResponse.data.message || "Enquiry submitted successfully!");
        setFormData({
          contact_name: "",
          contact_email: "",
          contact_no: "",
          contact_message: "",
        });
        if (onHide) onHide();
      } else {
        toast.error(enquiryResponse.data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form className={variant}>
      <div className="name-field">
        <FloatingLabel controlId={nameId} label="Your name">
          <Form.Control
            required
            type="text"
            name="contact_name"
            value={formData.contact_name}
            onChange={handleChange}
            placeholder="enter your name"
            className={error?.contact_name ? "is-invalid" : ""}
          />
        </FloatingLabel>
        <Form.Control.Feedback type="invalid">{error?.contact_name}</Form.Control.Feedback>
      </div>

      <div className="mobile-number-field">
        <FloatingLabel controlId={phoneId} label="Phone Number">
          <Form.Control
            required
            type="text"
            name="contact_no"
            value={formData.contact_no}
            onChange={handleChange}
            placeholder="Enter Your Phone Number"
            className={error?.contact_no ? "is-invalid" : ""}
          />
        </FloatingLabel>
        <Form.Control.Feedback type="invalid">{error?.contact_no}</Form.Control.Feedback>
      </div>

      <div className="email-field">
        <FloatingLabel controlId={emailId} label="Your Email">
          <Form.Control
            required
            type="email"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleChange}
            placeholder="Enter Your Email"
            className={error?.contact_email ? "is-invalid" : ""}
          />
        </FloatingLabel>
        <Form.Control.Feedback type="invalid">{error?.contact_email}</Form.Control.Feedback>
      </div>

      <div className="text-area-field">
        <FloatingLabel controlId={messageId} label="Message">
          <Form.Control
            required
            as="textarea"
            name="contact_message"
            value={formData.contact_message}
            onChange={handleChange}
            placeholder="Enter Your Message ..."
            className={error?.contact_message ? "is-invalid" : ""}
            style={{
              minHeight: "100px",
            }}
          />
        </FloatingLabel>
        <Form.Control.Feedback type="invalid">{error?.contact_message}</Form.Control.Feedback>
      </div>
      <div className="submit-btn">
        <CustomButton
          text={loading ? "Submitting Message..." : "Submit Message"}
          type="submit"
          onClick={handleSubmit}
        />
      </div>
    </Form>
  );
};

export default CustomForm;
